import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Link, Check, Pencil, FolderInput } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion } from 'framer-motion';

type ClassData = {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  video_url: string;
  categories: string[];
  created_at: string;
};

type Category = {
  id: string;
  name: string;
};

interface ClassCardProps {
  class_: ClassData;
  categories: Category[];
  isAdmin: boolean;
  copiedClassId: string | null;
  onCopyLink: (classId: string) => void;
  onEdit: (class_: ClassData) => void;
  onMoveToFolder?: (class_: ClassData) => void;
  index: number; // For staggered animation
}

const ClassCard = ({ class_, categories, isAdmin, copiedClassId, onCopyLink, onEdit, onMoveToFolder, index = 0 }: ClassCardProps) => {
  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [tiltValues, setTiltValues] = useState({ x: 0, y: 0 });
  const [ripple, setRipple] = useState({ active: false, x: 0, y: 0 });
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);
  
  // Random completion percentage between 0-100 for demo
  // In a real app, this would come from user progress data
  const [completionPercentage] = useState(
    Math.random() > 0.3 ? Math.floor(Math.random() * 100) : undefined
  );

  // Check user's motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const formatTime = (timestamp: string) => {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (prefersReducedMotion || !cardRef.current) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    
    // Calculate mouse position relative to card center (range: -1 to 1)
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    
    // Set tilt values (max 4 degrees)
    setTiltValues({
      x: y * -4, // Invert Y for natural tilt
      y: x * 4
    });
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (cardRef.current) {
      // Create ripple effect
      const rect = cardRef.current.getBoundingClientRect();
      setRipple({
        active: true,
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      
      // Start card expansion after ripple
      setTimeout(() => {
        setIsExpanding(true);
      }, 180);
      
      // Navigate after expansion animation
      setTimeout(() => {
        navigate(`/classroom/${class_.id}`);
      }, 500);
    }
  };

  // Custom link renderer for markdown
  const components = {
    a: ({ href, ...props }: { href?: string; [key: string]: any }) => {
      if (!href) return <span {...props} />;
      
      const isAbsolute = /^https?:\/\//i.test(href);
      const absoluteUrl = isAbsolute ? href : `https://${href}`;

      return (
        <a 
          className="text-[#c9fffc] hover:text-[#a0f0ff] underline"
          href={absoluteUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          {...props} 
        />
      );
    }
  };

  const cubicBezier = [0.33, 1, 0.68, 1];

  // Apply different animation settings based on user's motion preference
  const motionProps = {
    card: {
      whileHover: prefersReducedMotion ? {} : { 
        scale: 1.02, 
        boxShadow: '0 12px 20px -5px rgba(0, 0, 0, 0.3), 0 8px 16px -8px rgba(0, 0, 0, 0.4)' 
      },
      whileTap: prefersReducedMotion ? {} : { scale: 0.98 },
      initial: { opacity: 0, y: prefersReducedMotion ? 0 : 20 },
      animate: { opacity: 1, y: 0 },
      transition: { 
        duration: 0.5, 
        delay: prefersReducedMotion ? 0 : index * 0.05,
        ease: cubicBezier
      }
    }
  };

  return (
    <motion.div
      className="relative bg-[#151D29] rounded-xl overflow-hidden shadow-lg h-full"
      ref={cardRef}
      style={{
        perspective: prefersReducedMotion ? 'none' : '1000px',
        transformStyle: 'preserve-3d',
      }}
      onMouseMove={prefersReducedMotion ? undefined : handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setTiltValues({ x: 0, y: 0 });
      }}
      onClick={(e) => {
        if (!(e.target as HTMLElement).closest('button')) {
          handleCardClick(e);
        }
      }}
      {...motionProps.card}
    >
      {/* Card container */}
      <motion.div 
        className="relative w-full h-full"
        style={{
          transformStyle: 'preserve-3d',
          transform: prefersReducedMotion 
            ? 'none' 
            : `perspective(1000px) rotateX(${tiltValues.x}deg) rotateY(${tiltValues.y}deg)`
        }}
        animate={{
          boxShadow: isHovered 
            ? '0 12px 20px -5px rgba(0, 0, 0, 0.3), 0 8px 16px -8px rgba(0, 0, 0, 0.4)' 
            : '0 4px 12px -2px rgba(0, 0, 0, 0.2)',
        }}
        transition={{ duration: 0.16, ease: cubicBezier }}
      >
        {/* Ripple effect */}
        {ripple.active && !prefersReducedMotion && (
          <motion.div
            className="absolute rounded-full bg-[#0E1621]"
            initial={{ 
              x: ripple.x, 
              y: ripple.y, 
              width: 0, 
              height: 0,
              opacity: 0.4
            }}
            animate={{ 
              width: 600, 
              height: 600,
              x: ripple.x - 300, 
              y: ripple.y - 300,
              opacity: 0
            }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            style={{ zIndex: 10 }}
          />
        )}

        {/* Thumbnail image area */}
        <div 
          className="aspect-video relative cursor-pointer overflow-hidden" 
          style={{ 
            transformStyle: 'preserve-3d',
            transform: 'translateZ(5px)'
          }}
        >
          {/* Episode number and progress bar */}
          <motion.div 
            className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-80 px-3 py-1.5 z-10"
            animate={{ 
              y: isHovered && completionPercentage !== undefined ? 6 : 0,
            }}
            transition={{ duration: 0.16, ease: cubicBezier }}
          >
            <div className="flex justify-between items-center">
              <span className="text-xs font-medium text-white">
                {categories.filter(c => class_.categories.includes(c.id)).map(c => c.name).join(', ')}
              </span>
            </div>
          </motion.div>

          {/* Progress bar (only shown if completed and on hover) */}
          {completionPercentage !== undefined && (
            <motion.div 
              className="absolute bottom-0 left-0 right-0 h-1.5 bg-gray-800 z-20"
              initial={{ opacity: 0 }}
              animate={{ 
                opacity: isHovered ? 1 : 0,
              }}
              transition={{ duration: 0.16, ease: cubicBezier }}
            >
              <motion.div 
                className="h-full bg-[#c9fffc]"
                initial={{ width: 0 }}
                animate={{ 
                  width: isHovered ? `${completionPercentage}%` : 0 
                }}
                transition={{ duration: 0.3, ease: cubicBezier }}
              />
            </motion.div>
          )}

          {/* Image with zoom effect */}
          <motion.div
            className="w-full h-full"
            animate={{ 
              scale: isHovered && !prefersReducedMotion ? 1.04 : 1,
              clipPath: isExpanding ? 'inset(0% 0% 0% 0%)' : 'none'
            }}
            transition={{ 
              scale: { duration: 0.16, ease: cubicBezier },
              clipPath: { duration: 0.5, ease: cubicBezier }
            }}
            style={{ 
              position: isExpanding ? 'fixed' : 'relative',
              top: isExpanding ? 0 : 'auto',
              left: isExpanding ? 0 : 'auto',
              width: isExpanding ? '100%' : '100%',
              height: isExpanding ? '100%' : '100%',
              zIndex: isExpanding ? 50 : 1
            }}
          >
            <img
              src={class_.thumbnail}
              alt={class_.title}
              className="w-full h-full object-cover rounded-t-md"
              style={{ borderRadius: '4px 4px 0 0' }}
            />
          </motion.div>

          {/* Play button overlay */}
          <motion.div 
            className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.16, ease: cubicBezier }}
          >
            <motion.div 
              className="w-16 h-16 rounded-full bg-white bg-opacity-90 flex items-center justify-center"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ 
                transformStyle: 'preserve-3d',
                transform: 'translateZ(15px)'
              }}
            >
              <Play className="w-8 h-8 text-gray-900 ml-1" />
            </motion.div>
          </motion.div>
        </div>

        {/* Content area */}
        <motion.div 
          className="p-6"
          style={{ 
            transformStyle: 'preserve-3d',
            transform: 'translateZ(5px)'
          }}
        >
          {/* Title with opacity animation */}
          <motion.h3 
            className="text-[20px] leading-[28px] font-medium text-white mb-2 tracking-tight"
            animate={{ opacity: isHovered ? 1 : 0.7 }}
            transition={{ duration: 0.16, ease: cubicBezier }}
          >
            {class_.title}
          </motion.h3>

          {/* Description with slide-up animation */}
          <motion.div 
            className="prose prose-invert prose-sm max-w-none text-[14px] leading-[22px] tracking-tight"
            animate={{ y: isHovered ? -8 : 0, opacity: isHovered ? 1 : 0.8 }}
            transition={{ duration: 0.16, ease: cubicBezier }}
          >
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={components}
            >
              {class_.description.length > 100 
                ? `${class_.description.substring(0, 100)}...` 
                : class_.description}
            </ReactMarkdown>
          </motion.div>

          {/* Footer with metadata and actions */}
          <motion.div 
            className="mt-4 flex justify-between items-center"
            animate={{ y: isHovered ? -8 : 0, opacity: isHovered ? 1 : 0.7 }}
            transition={{ duration: 0.16, ease: cubicBezier }}
          >
            <span className="text-[14px] text-gray-400">
              {formatTime(class_.created_at)}
            </span>

            <div className="flex items-center gap-2">
              <motion.button
                onClick={(e) => {
                  e.stopPropagation();
                  onCopyLink(class_.id);
                }}
                className="p-2 bg-[#1a2533] rounded-full hover:bg-[#232f3e] transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0 }}
                animate={{ opacity: isHovered ? 1 : 0 }}
                transition={{ duration: 0.1, ease: cubicBezier }}
              >
                {copiedClassId === class_.id ? (
                  <Check className="w-4 h-4 text-green-400" />
                ) : (
                  <Link className="w-4 h-4 text-[#c9fffc]" />
                )}
              </motion.button>

              {onMoveToFolder && (
                <motion.button
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveToFolder(class_);
                  }}
                  className="p-2 bg-[#1a2533] rounded-full hover:bg-[#232f3e] transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: isHovered ? 1 : 0 }}
                  transition={{ duration: 0.1, ease: cubicBezier }}
                >
                  <FolderInput className="w-4 h-4 text-[#c9fffc]" />
                </motion.button>
              )}

              {isAdmin && (
                <motion.button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(class_);
                  }}
                  className="p-2 bg-[#1a2533] rounded-full hover:bg-[#232f3e] transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: isHovered ? 1 : 0 }}
                  transition={{ duration: 0.1, ease: cubicBezier }}
                >
                  <Pencil className="w-4 h-4 text-[#c9fffc]" />
                </motion.button>
              )}
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default ClassCard;
